import { CloseCircleOutlined, FilterOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Form,
  Radio,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import dayjs from 'dayjs';

import { MultipleSelect } from '@app/components';
import {
  DATE_PICKER_FORMAT,
  ISO_FORMAT_INDEX,
  OFF,
  PREFIX,
  rangePickerLocale,
  today,
  fistDayOfCurrentMonth,
} from '@app/constants';
import { ChartComponent } from '@app/pages/Analytics/OrganizationClientsGrowth/ChartComponent/ChartComponent';
import { useOrganizationClientsGrowth } from '@app/pages/Analytics/OrganizationClientsGrowth/hooks/useOrganizationClientsGrowth';
import { useOrganizationClientsGrowthEffects } from '@app/pages/Analytics/OrganizationClientsGrowth/hooks/useOrganizationClientsGrowthEffects';
import type { FormValues } from '@app/pages/Analytics/OrganizationClientsGrowth/OrganizationClientsGrowth.types';

import type { Dayjs } from 'dayjs';

import '@app/pages/Analytics/OrganizationClientsGrowth/styles/OrganizationClientsGrowth.desktop.scss';

const { Text } = Typography;
const { RangePicker } = DatePicker;

export const OrganizationClientsGrowth: React.FC = () => {
  const {
    selectedBranches,
    setSelectedBranches,
    form,
    submitForm,
    radioGroupOptions,
    handleDatePickerValueChange,
    handleRadioGroupValueChange,
    chartDataParams,
    handleReset,
  } = useOrganizationClientsGrowth();
  const { branches, isLoading, organizationClientsGrowth } =
    useOrganizationClientsGrowthEffects({
      setSelectedBranches,
      chartDataParams,
    });

  if (isLoading) {
    return <Skeleton active />;
  }

  return (
    <div className={`${PREFIX}organization-client-growth`}>
      <div className="header-container">
        <Form
          form={form}
          autoComplete={OFF}
          onFinish={submitForm}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 30 }}
          style={{ marginBottom: 10 }}
          layout="vertical"
          initialValues={{
            Date: [
              fistDayOfCurrentMonth.format(
                DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]
              ),
              today.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]),
            ],
          }}
        >
          <div className="date-container">
            <div className="date-item">
              <Text className="label" strong>
                Период
              </Text>
              <Form.Item
                name="Date"
                getValueProps={(value?: FormValues['Date']) => ({
                  value: value?.map((item) =>
                    dayjs(item, DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
                  ),
                })}
                normalize={(value?: Dayjs[]) =>
                  value?.map((item: Dayjs) =>
                    item.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
                  )
                }
              >
                <RangePicker
                  format={DATE_PICKER_FORMAT}
                  size="middle"
                  allowEmpty={[false, false]}
                  locale={rangePickerLocale}
                  onChange={handleDatePickerValueChange}
                  allowClear={false}
                />
              </Form.Item>
            </div>
            <Form.Item name="RadioGroup">
              <Radio.Group
                block
                options={radioGroupOptions}
                optionType="button"
                buttonStyle="solid"
                onChange={handleRadioGroupValueChange}
              />
            </Form.Item>
          </div>

          <div className="selectors-container">
            <div className="selectors">
              <MultipleSelect
                defaultSelected={Boolean(branches?.length)}
                className="user-selector"
                placeholder="Все филиалы"
                allOptions={(branches || []).map(({ Name, Id }) => {
                  return {
                    label: Name,
                    value: Id,
                  };
                })}
                width={100}
                selectedOption={selectedBranches}
                setSelectedOptions={setSelectedBranches}
              />
            </div>

            <Form.Item
              style={{
                display: 'inline-block',
                marginBottom: 0,
              }}
              className="buttons-container"
            >
              <Space className="desktop-buttons" size="middle">
                <Button
                  htmlType="reset"
                  onClick={handleReset}
                  className="button"
                >
                  <CloseCircleOutlined />
                  Сброс
                </Button>

                <Button type="primary" htmlType="submit" className="button">
                  <FilterOutlined />
                  Показать
                </Button>
              </Space>
            </Form.Item>
          </div>
        </Form>
      </div>

      <ChartComponent organizationClientsGrowth={organizationClientsGrowth} />
    </div>
  );
};
